import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Save, Loader2, MessageSquarePlus, Tag, AlertCircle } from 'lucide-react';
import { FeedbackResponseDto } from '../../Admin/Types/FeedbackType';
import { createFeedback, updateFeedbackByUser } from '../../Admin/Services/AdminService';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  feedback: FeedbackResponseDto | null;
  currentUserId: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

interface FormState {
  category: string;
  feedbackPriority: string;
  subject: string;
  message: string;
  status: string;
}

const emptyForm: FormState = {
  category: 'BUG',
  feedbackPriority: 'MEDIUM',
  subject: '',
  message: '',
  status: 'OPEN',
};

const CATEGORY_OPTIONS = [
  { label: 'Bug', value: 'BUG' },
  { label: 'Support', value: 'SUPPORT' },
  { label: 'Feature Request', value: 'FEATURE_REQUEST' },
  { label: 'General', value: 'GENERAL' },
  { label: 'Complaint', value: 'COMPLAINT' },
  { label: 'Other', value: 'OTHER' },
];

const PRIORITY_OPTIONS = [
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
  { label: 'Critical', value: 'CRITICAL' },
];

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const FeedbackFormModal = ({
  isOpen,
  mode,
  feedback,
  currentUserId,
  onClose,
  onSaved,
}: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;

    setFormError('');
    setSaving(false);

    if (mode === 'edit' && feedback) {
      setForm({
        category: feedback.category || 'BUG',
        feedbackPriority: feedback.feedbackPriority || 'MEDIUM',
        subject: feedback.subject || '',
        message: feedback.message || '',
        status: feedback.status || 'OPEN',
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, mode, feedback]);

  const canReopen = feedback?.status === 'CLOSED' || feedback?.status === 'RESOLVED';

  const title = useMemo(
    () => (mode === 'create' ? 'Report Feedback' : 'Edit Feedback'),
    [mode]
  );

  const handleSubmit = async () => {
    if (!form.subject.trim()) {
      setFormError('Subject is required.');
      return;
    }

    if (!form.message.trim()) {
      setFormError('Message is required.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const response =
        mode === 'create'
          ? await createFeedback({
              category: form.category,
              subject: form.subject.trim(),
              message: form.message.trim(),
              feedbackPriority: form.feedbackPriority,
              userId: currentUserId,
            })
          : await updateFeedbackByUser(
              {
                feedbackId: feedback!.id,
                category: form.category,
                subject: form.subject.trim(),
                message: form.message.trim(),
                feedbackPriority: form.feedbackPriority,
                userId: currentUserId,
                status: form.status,
              },
              currentUserId
            );

      if (response.success) {
        onSaved(
          mode === 'create' ? 'Feedback reported successfully.' : 'Feedback updated successfully.'
        );
        onClose();
      } else {
        setFormError(response.message || 'Could not save the feedback.');
      }
    } catch (err) {
      console.error('Failed to save feedback', err);
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <MessageSquarePlus size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">{title}</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {mode === 'create'
                      ? 'Share an issue, request or suggestion with the team'
                      : 'Update the details of your reported feedback'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              {formError && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>
                    <Tag size={11} className="mr-1 inline" /> Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => set('category', e.target.value)}
                    className={fieldClass}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    <AlertCircle size={11} className="mr-1 inline" /> Priority
                  </label>
                  <select
                    value={form.feedbackPriority}
                    onChange={(e) => set('feedbackPriority', e.target.value)}
                    className={fieldClass}
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Subject *</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => set('subject', e.target.value)}
                    placeholder="Short summary of your feedback"
                    className={fieldClass}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Message *</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => set('message', e.target.value)}
                    placeholder="Describe the issue or request in detail..."
                    className={`${fieldClass} resize-none`}
                  />
                </div>

                {mode === 'edit' && canReopen && (
                  <div className="md:col-span-2">
                    <label className={labelClass}>Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => set('status', e.target.value)}
                      className={fieldClass}
                    >
                      <option value={feedback?.status} className="bg-slate-900 text-slate-100">
                        {feedback?.status} (current)
                      </option>
                      <option value="REOPEN" className="bg-slate-900 text-slate-100">
                        REOPEN
                      </option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? 'Saving' : mode === 'create' ? 'Submit Feedback' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackFormModal;
